import React, { useEffect, useState } from 'react';
import './WellAlarms.scss';
import AlarmHistoryTable from '../data-tables/AlarmHistoryTable';
import TableHeader from '../data-tables/TableHeader';
import ExportTable from '../common/export-table/ExportTable';
import { RootState } from '../../store';
import { fetchAlarmDataAsync } from '../slices/AlarmHistorySlice';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { useParams } from 'react-router-dom';
import Loader from '../../features/common/Loader/Loader';
import { DASHBOARDS, R, WIDGETS } from '../../rbac';
import { ProtectComponent } from '../../rbac';
import { useRBAC } from '../../rbac/RbacProvider';
import { formatDate } from '../../utilities/dateHelpers';

const WellAlarms: React.FC = () => {
  const dispatch = useAppDispatch();
  const { assetId } = useParams<{ assetId: string }>();
  const alarmData = useAppSelector((state: RootState) => state.AlarmHistorySlice.data);
  const loading = useAppSelector((state: RootState) => state.AlarmHistorySlice.loading);
  const selectedAssetId = useAppSelector((state) => state.assetGroups?.selectedAssetId);
  const widgetName = 'Alarm history';

  const { can } = useRBAC();
  const resource = R.widget(DASHBOARDS.ALARMS_DATA, WIDGETS.ALARMS_GRID);
  const rPermission = {
    canExport: can(resource, 'export'),
  };

  // Table data for export
  const [tableRows, setTableRows] = useState<any[]>([]);

  // Handle table data ready callback
  const handleTableDataReady = (rows: any[]) => {
    setTableRows(rows);
  };

  // Define export columns configuration
  const exportColumns = [
    {
      key: 'alarmDate',
      header: 'Date',
      accessor: (row: any) =>
        formatDate(row.values?.alarmDate || row.original?.alarmDate, true) || '',
    },
    {
      key: 'alarmSource',
      header: 'Source',
      accessor: (row: any) => row.values?.alarmSource || row.original?.alarmSource || '',
    },
    {
      key: 'alarmDescription',
      header: 'Description',
      accessor: (row: any) => row.values?.alarmDescription || row.original?.alarmDescription || '',
    },
  ];

  useEffect(() => {
    if (assetId) {
      dispatch(
        fetchAlarmDataAsync({
          assetId: assetId,
          startDate: undefined, //'06/01/2023 8:19:33 PM',
          endDate: undefined, //'06/06/2025 8:19:33 PM',
          filterBy: undefined,
          filterValue: undefined,
          orderBy: undefined,
          orderColumn: undefined,
          appendData: false,
        }),
      );
    }
  }, [dispatch, assetId]);

  return (
    <ProtectComponent
      resource={R.widget(DASHBOARDS.ALARMS_DATA, WIDGETS.ALARMS_GRID)}
      action='view'
      widgetName={widgetName}
    >
      <div className='well-alarms-container'>
        <main className='main-container table-wrap'>
          <TableHeader title={widgetName} />

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              margin: '16px 20px',
            }}
          >
            <ExportTable
              data={tableRows}
              fileName={`Alarm History-${selectedAssetId}`}
              sheetName='Alarm History'
              columns={exportColumns}
              disabled={loading || !tableRows || tableRows.length === 0}
              rPermission={rPermission}
            />
          </div>

          {loading ? (
            <div className='alarm-loading-state'>
              <Loader />
            </div>
          ) : (
            <div className='alarm-table-wrapper'>
              <AlarmHistoryTable alarmHistoryItems={alarmData || []} onDataReady={handleTableDataReady} />
            </div>
          )}
          <div className='alarm-status-legend'>
            <div className='alarm-legend-item'>
              <span className='alarm-dot alarm-set'></span>
              <span className='alarm-legend-text'>Alarm set</span>
            </div>
            <div className='alarm-legend-item'>
              <span className='alarm-dot alarm-clear'></span>
              <span className='alarm-legend-text'>Alarm clear</span>
            </div>
          </div>
        </main>
      </div>
    </ProtectComponent>
  );
};

export default WellAlarms;
